<template>
  <app-client-layout title="成績">
    <template #header>
      <h2 class="text-xl font-semibold leading-tight text-gray-800">
        成績一覧
      </h2>
    </template>

    <div class="md:flex justify-center mx-auto">
      <div class="m-4 flex-none">
        <div class="col-span-4 shadow-lg bg-white mr-1 border border-gray-200 rounded-lg">
          <div class="p-4">
            <div class="flex justify-between mb-2">
              <div class="uppercase tracking-wide text-c2 mb-4">
                模試一覧
              </div>
            </div>
            <div
              v-for="(examination, i) in examinations"
              :key="i"
              :class="{
                'bg-indigo-500': selectedExaminationIndex === i,
                'text-white': selectedExaminationIndex === i,
              }">
              <template v-if="examination.is_published || isPurchased(examination.index)">
                <button
                  class="w-full"
                  @click="selectExamination(i)">
                  <div
                    class="flex cursor-pointer border py-2 text-lg text-left text-grey-darkest hover:bg-indigo-500 hover:text-white">
                    <div class="pl-2 pt-1 text-sm">
                      {{ examination.title }}
                    </div>
                  </div>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white my-4 shadow overflow-hidden sm:rounded-lg max-w-2xl">
        <div v-if="!isPurchased(examinations[selectedExaminationIndex].id)">
          <div class="px-4 py-5 sm:px-6">
            <h4 class="text-lg leading-6 font-medium text-gray-900">
              成績詳細
            </h4>
          </div>
          <div class="border-t border-gray-200">
            <dl>
              <div class="bg-gray-50 px-4 py-5">
                <dt class="text-md font-medium text-gray-500">
                  模試名
                </dt>
                <dd class="mt-2 ml-4 text-md text-gray-900">
                  {{ examinations[selectedExaminationIndex].title }}
                </dd>
              </div>
              <div class="bg-white px-4 py-5">
                <dt class="text-md font-medium text-gray-500">
                  成績
                </dt>
                <dd class="mt-2 ml-4 text-xl text-red-500">
                  模試未購入のため閲覧できません。
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div v-else>
          <div class="px-4 py-5 sm:px-6">
            <h4 class="text-lg leading-6 font-medium text-gray-900">
              成績詳細
            </h4>
          </div>
          <div class="border-t border-gray-200">
            <dl>
              <div class="bg-gray-50 px-4 py-5">
                <dt class="text-md font-medium text-gray-500">
                  模試名
                </dt>
                <dd class="mt-2 ml-4 text-md text-gray-900">
                  {{ examinations[selectedExaminationIndex].title }}
                </dd>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-8 bg-white px-4 py-5">
                <dt class="text-md col-span-5 font-medium text-gray-500">
                  成績
                  <div
                    v-if="isFinishSelectType(examinations[selectedExaminationIndex].id, 1)"
                    class="mt-2 ml-4 grid grid-cols-4 items-center text-center">
                    <p class="inline text-gray-900 text-right">
                      必須：
                    </p>
                    <p
                      v-if="isOveredAggregationPeriod(examinations[selectedExaminationIndex], 1) && getRanking(examinations[selectedExaminationIndex].id) && isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
                      class="inline font-black text-xl sm:text-2xl text-right mr-2">
                      ({{ getScore(examinations[selectedExaminationIndex].id, 1) }})
                    </p>
                    <p
                      v-else
                      class="inline font-black text-xl sm:text-2xl text-right mr-2">
                      {{ getScore(examinations[selectedExaminationIndex].id, 1) }}
                    </p>
                    <p class="inline text-left ml-2">
                      /50
                    </p>
                    <p :class="getRankColor(getRank(examinations[selectedExaminationIndex].id, 1))">
                      {{ getRank(examinations[selectedExaminationIndex].id, 1) }}
                    </p>
                  </div>
                  <div
                    v-else
                    class="mt-4 ml-4 grid grid-cols-4 items-center">
                    <p class="inline text-gray-900 text-right">
                      必須：
                    </p>
                    <p class="inline font-black text-md ml-2 text-right">
                      未実施
                    </p>
                  </div>
                  <div
                    v-if="isFinishSelectType(examinations[selectedExaminationIndex].id, 2)"
                    class="mt-2 ml-4 grid grid-cols-4 items-center text-center">
                    <p class="inline text-gray-900 text-right">
                      一般：
                    </p>
                    <p
                      v-if="isOveredAggregationPeriod(examinations[selectedExaminationIndex], 2) && getRanking(examinations[selectedExaminationIndex].id) && isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
                      class="inline font-black text-xl sm:text-2xl text-right mr-2">
                      ({{ getScore(examinations[selectedExaminationIndex].id, 2) }})
                    </p>
                    <p
                      v-else
                      class="inline font-black text-xl sm:text-2xl text-right mr-2">
                      {{ getScore(examinations[selectedExaminationIndex].id, 2) }}
                    </p>
                    <p class="inline text-left ml-2">
                      /100
                    </p>
                    <p :class="getRankColor(getRank(examinations[selectedExaminationIndex].id, 2))">
                      {{ getRank(examinations[selectedExaminationIndex].id, 2) }}
                    </p>
                  </div>
                  <div
                    v-else
                    class="mt-5 ml-4 grid grid-cols-4 items-center">
                    <p class="inline text-gray-900 text-right">
                      一般：
                    </p>
                    <p class="inline font-black text-md ml-2 text-right">
                      未実施
                    </p>
                  </div>
                  <div
                    v-if="isFinishSelectType(examinations[selectedExaminationIndex].id, 3)"
                    class="mt-2 ml-4 grid grid-cols-4 items-center text-center">
                    <p class="inline text-gray-900 text-right">
                      実地：
                    </p>
                    <p
                      v-if="isOveredAggregationPeriod(examinations[selectedExaminationIndex], 3) && getRanking(examinations[selectedExaminationIndex].id) && isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
                      class="inline font-black text-xl sm:text-2xl text-right mr-2">
                      ({{ getScore(examinations[selectedExaminationIndex].id, 3) }})
                    </p>
                    <p
                      v-else
                      class="inline font-black text-xl sm:text-2xl text-right mr-2">
                      {{ getScore(examinations[selectedExaminationIndex].id, 3) }}
                    </p>
                    <p class="inline text-left ml-2">
                      /50
                    </p>
                    <p :class="getRankColor(getRank(examinations[selectedExaminationIndex].id, 3))">
                      {{ getRank(examinations[selectedExaminationIndex].id, 3) }}
                    </p>
                  </div>
                  <div
                    v-else
                    class="mt-5 ml-4 grid grid-cols-4 items-center">
                    <p class="inline text-gray-900 text-right">
                      実地：
                    </p>
                    <p class="inline font-black text-md ml-2 text-right">
                      未実施
                    </p>
                  </div>
                  <div
                    v-if="isFinishExam(examinations[selectedExaminationIndex].id)"
                    class="mt-6 ml-4 grid grid-cols-4 items-center text-center">
                    <p class="inline text-gray-900 text-right">
                      合計：
                    </p>
                    <p class="inline font-black text-2xl sm:text-4xl text-right mr-2">
                      {{ getTotalScore(examinations[selectedExaminationIndex].id) }}
                    </p>
                    <p class="inline text-left ml-2">
                      /200
                    </p>
                    <p :class="getRankColor(calcRank(getTotalScore(examinations[selectedExaminationIndex].id), 200))">
                      {{ calcRank(getTotalScore(examinations[selectedExaminationIndex].id), 200) }}
                    </p>
                  </div>
                  <div
                    v-else
                    class="mt-8 ml-4 grid grid-cols-4 items-center">
                    <p class="inline text-gray-900 text-right">
                      合計：
                    </p>
                    <p class="inline font-black text-md ml-2 text-right">
                      未実施
                    </p>
                  </div>
                  <div class="mt-6 ml-4 grid grid-cols-4 items-center text-center">
                    <p
                      v-if="examinations[selectedExaminationIndex].result_announcement_date"
                      class="inline text-gray-900 text-right">
                      順位：
                    </p>
                    <p
                      v-if="getRanking(examinations[selectedExaminationIndex].id)"
                      class="inline font-black text-2xl sm:text-4xl text-right mr-2">
                      {{ getRanking(examinations[selectedExaminationIndex].id) }}位
                    </p>
                    <p
                      v-if="getRanking(examinations[selectedExaminationIndex].id)"
                      class="inline text-left ml-2">
                      /{{ getUserNum(examinations[selectedExaminationIndex].id) }}人中
                    </p>
                    <p
                      v-else-if="examinations[selectedExaminationIndex].result_announcement_date"
                      class="inline font-black text-md ml-2 text-right">
                      未集計
                    </p>
                  </div>
                </dt>
                <dd class="grid mt-4 sm:mt-0 sm:col-span-3 text-sm text-gray-900">
                  <BarChart :chart-data="getBarData(examinations[selectedExaminationIndex].id)" />
                </dd>
                <p
                  v-if="isBeforeDate(examinations[selectedExaminationIndex].result_announcement_date)"
                  class="mt-2 grid col-span-5 text-sm text-red-500">
                  ※順位と平均点は後日集計予定となります。
                </p>
                <p
                  v-if="isOveredDate(examinations[selectedExaminationIndex].result_announcement_date) && isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
                  class="grid col-span-5 text-sm text-red-500">
                  ※点数が()付きで表記されている科目につきましては合計点数の計算対象外となります。
                </p>
                <p
                  v-if="isBeforeDate(examinations[selectedExaminationIndex].result_announcement_date) && !isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
                  class="grid col-span-5 text-sm text-red-500">
                  成績集計開始日時：{{ formatDate(examinations[selectedExaminationIndex].result_announcement_date) }}
                </p>
              </div>
              <div class="bg-gray-50 px-4 py-5 mt-4">
                <div
                  v-if="isFinishAllType(examinations[selectedExaminationIndex].id)"
                  class="flex">
                  <button
                    class="bg-green-600 block py-3 px-5 lg:hover:bg-green-400 active:bg-green-400 duration-300 text-white rounded-lg"
                    type="button"
                    @click="onClickCommentary(examinations[selectedExaminationIndex].id, 1)">
                    解説ページへ
                  </button>
                  <a
                    class="bg-yellow-500 block ml-8 py-3 px-5 lg:hover:bg-yellow-400 active:bg-green-400 duration-300 text-white rounded-lg"
                    :href="route('commentary.answer-list', { examination: examinations[selectedExaminationIndex].id })"
                    target="_blank">
                    解答一覧
                  </a>
                </div>
                <button
                  v-else
                  class="bg-gray-600 block text-xs py-3 px-5 opacity-50 cursor-not-allowed duration-300 text-white rounded-lg"
                  type="button">
                  未実施の種別があります
                </button>
                <div v-if="selectedExaminationMovieUrls.length">
                  <h1 class="mt-3 font-bold">解説動画</h1>
                  <div
                    v-for="(movieUrl, index) in selectedExaminationMovieUrls"
                    :key="index">
                    <a
                      class="flex items-center text-blue-600 text-base"
                      :href="movieUrl.url"
                      target="_blank">
                      {{ movieUrl.title }}
                    </a>
                  </div>
                </div>
                <h1 class="mt-3 font-bold">問題と解答のダウンロード</h1>
                <table
                  v-if="isFinishAllType(examinations[selectedExaminationIndex].id)"
                  class="text-sm text-left">
                  <tr>
                    <th class="font-normal">一般の問題</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="onDownloadPdf(
                          examinations[selectedExaminationIndex].id,
                          EXAMINATION_TYPES.general.label,
                          '問題',
                        )">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        PDFをダウンロード
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <th class="font-normal">一般の解答</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="onDownloadPdf(
                          examinations[selectedExaminationIndex].id,
                          EXAMINATION_TYPES.general.label,
                          '解答',
                        )">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        PDFをダウンロード
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <th class="font-normal">必須の問題</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="onDownloadPdf(
                          examinations[selectedExaminationIndex].id,
                          EXAMINATION_TYPES.compulsory.label,
                          '問題',
                        )">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        PDFをダウンロード
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <th class="font-normal">必須の解答</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="onDownloadPdf(
                          examinations[selectedExaminationIndex].id,
                          EXAMINATION_TYPES.compulsory.label,
                          '解答',
                        )">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        PDFをダウンロード
                      </a>
                    </td>
                  </tr>
                  <tr v-if="examinations[selectedExaminationIndex].index >= 7">
                    <th class="font-normal">実地の問題</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="onDownloadPdf(
                          examinations[selectedExaminationIndex].id,
                          EXAMINATION_TYPES.practice.label,
                          '問題',
                        )">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        PDFをダウンロード
                      </a>
                    </td>
                  </tr>
                  <tr v-if="examinations[selectedExaminationIndex].index >= 7">
                    <th class="font-normal">実地の解答</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="onDownloadPdf(
                          examinations[selectedExaminationIndex].id,
                          EXAMINATION_TYPES.practice.label,
                          '解答',
                        )">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        PDFをダウンロード
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <th class="font-normal">まとめてダウンロード</th>
                    <td>
                      <a
                        class="flex items-center text-blue-600 ml-3 text-xs rounded-lg"
                        :href="route('result.downloadZip', {
                          examination_id: examinations[selectedExaminationIndex].id,
                        })">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                        </svg>
                        ZIPをダウンロード
                      </a>
                    </td>
                  </tr>
                </table>
                <div
                  v-else
                  class="mt-2 text-xs text-red-500 whitespace-pre-line">
                  未実施の種別があります。<br />
                  問題と解説のPDFは全模試種別実施後にダウンロード可能となります。
                </div>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </app-client-layout>
</template>

<script setup>
import moment from 'moment'
import Swal from 'sweetalert2'
import { ref, computed } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { Chart, registerables } from 'chart.js'
import { BarChart } from 'vue-chart-3'
import formatDate from '@/actions/format/date'
import AppClientLayout from '@/Layouts/AppClientLayout'
import { EXAMINATION_TYPES } from '@/Constants/examinationTypes'

Chart.register(...registerables)

const props = defineProps({
  examinations: {
    type: Array,
    default: () => [],
  },
  examinationMovieUrls: {
    type: Array,
    default: () => [],
  },
  progresses: {
    type: Array,
    default: () => [],
  },
  purchases: {
    type: Array,
    default: () => [],
  },
  scores: {
    type: Array,
    default: () => [],
  },
  ranking: {
    type: Object,
    default: () => { },
  },
  rankings: {
    type: Array,
    default: () => [],
  },
  isAnsweredQuestionnaire: {
    type: Boolean,
    default: false,
  },
})

const selectedExaminationIndex = ref(0)

const selectExamination = (index) => {
  selectedExaminationIndex.value = index
}

const selectedExaminationMovieUrls = computed(() => props.examinationMovieUrls.filter((e) => e.examination_id === props.examinations[selectedExaminationIndex.value].id))

const isPurchased = (id) => props.purchases.filter(purchase => (purchase.exam_id === id)).length

const isFinishAllType = (id) => {
  const progresses = props.progresses.filter(progress => (progress.examination_id === id))

  if (progresses.length < 3) {
    return false
  }

  const isFinishAll = progresses.every(element => element.finished_at !== null)
  return isFinishAll
}

const isFinishSelectType = (id, selectType) => !!props.progresses.filter(progress => (progress.examination_id === id && progress.examination_type === selectType)).length

const isFinishExam = (id) => props.progresses.some(progress => (progress.examination_id === id))

const getScore = (id, type) => {
  const value = props.progresses.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  let score = 0
  if (value && value.score !== null) {
    score = value.score
  } else {
    score = 0
  }

  return score
}

const getScorePercentage = (id, type) => {
  const value = props.progresses.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  let score = 0
  if (value && value.score !== null) {
    if (type === 1 || type === 3) {
      return value.score * 2
    }
    return value.score
  }
  score = 0

  return score
}

const getTotalScore = (id) => {
  let totalScore = 0
  const ranking = props.ranking.find((value) => (value.examination_id === id))

  if (ranking == null) {
    for (let i = 1; i <= 3; i += 1) {
      const prog = props.progresses.filter(progress => (progress.examination_id === id)
        && (progress.examination_type === i)).pop()

      if (prog && prog.score !== null) {
        totalScore += prog.score
      } else {
        totalScore += 0
      }
    }
  } else {
    totalScore = ranking.score
  }

  return totalScore
}

const getAverageScorePercentage = (id, type) => {
  const value = props.scores.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  if (value && Object.keys(value).some(x => x === 'average_score')) {
    if (type === 1 || type === 3) {
      return value.average_score * 2
    }
    return value.average_score
  }
  return 0
}

const calcRank = (val, maxVal) => {
  const percentage = (val * 100) / maxVal

  let rank = ''
  if (percentage >= 80) {
    rank = 'A'
  } else if (percentage >= 60) {
    rank = 'B'
  } else if (percentage >= 40) {
    rank = 'C'
  } else {
    rank = 'D'
  }

  return rank
}

const getRank = (id, type) => {
  const prog = props.progresses.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  let intScore = 0
  if (prog && prog.score) {
    intScore = parseInt(prog.score, 10)
    if (type === 1 || type === 3) {
      intScore *= 2
    }
  } else {
    intScore = 0
  }

  return calcRank(intScore, 100)
}

const getRankColor = (rank) => {
  let color = ''
  if (rank === 'A') {
    color = 'text-yellow-500 inline font-black underline flex items-center text-4xl text-left'
  } else if (rank === 'B') {
    color = 'text-green-500 inline font-black underline flex items-center text-4xl text-left'
  } else if (rank === 'C') {
    color = 'text-yellow-300 inline font-black underline flex items-center text-4xl text-left'
  } else {
    color = 'text-blue-500 inline font-black underline flex items-center text-4xl text-left'
  }
  return color
}

const getUserNum = (id) => props.rankings.filter(ranking => (ranking.examination_id === id)).length

const getRanking = (id) => {
  const ranking = props.ranking.find((value) => (value.examination_id === id))
  if (ranking == null) {
    return false
  }
  return ranking.ranking
}

const onClickCommentary = (id) => {
  if (!props.isAnsweredQuestionnaire) {
    Swal.fire({
      icon: 'question',
      text: 'よろしければアンケートにご協力ください。',
      showDenyButton: true,
      confirmButtonText: '回答する',
      denyButtonText: 'スキップする',
    }).then((result) => {
      if (result.isConfirmed) {
        Inertia.get(route('questionnaire', {
          examination: id,
        }))
      } else if (result.isDenied) {
        Inertia.get(route('commentary', {
          examination: id,
          type: 1,
        }))
      }
    })
  } else {
    Inertia.get(route('commentary', {
      examination: id,
      type: 1,
    }))
  }
}

const getBarData = (id) => {
  const barData = {
    labels: ['必須', '一般', '実地'],
    datasets: [
      {
        type: 'bar',
        label: '得点(%)',
        data: [getScorePercentage(id, 1), getScorePercentage(id, 2), getScorePercentage(id, 3)],
        fill: true,
        order: 0,
        backgroundColor: [
          'rgba(54, 162, 235, 0.2)',
        ],
        borderColor: [
          'rgb(54, 162, 235)',
        ],
        borderWidth: 2,
      },
      {
        type: 'bar',
        label: '平均点(%)',
        data: [getAverageScorePercentage(id, 1), getAverageScorePercentage(id, 2), getAverageScorePercentage(id, 3)],
        fill: false,
        order: 1,
      },
    ],
  }

  return barData
}

const isBeforeDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isBefore(momentDate)
}

const isOveredDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isAfter(momentDate)
}

const isOveredAggregationPeriod = (examination, type) => {
  const prog = props.progresses.filter(progress => (progress.examination_id === examination.id)
    && (progress.examination_type === type)).pop()

  if (!prog) {
    return false
  }

  const progStartAt = moment(prog.started_at)
  const resultAnnouncementDate = moment(examination.result_announcement_date)

  return progStartAt.isAfter(resultAnnouncementDate)
}

const isBeforeFY2024 = (date) => {
  // 2024年度以前であればtrueを返す
  const FY2024 = moment('2024-04-01 00:00:00')
  const momentDate = moment(date)
  return momentDate.isBefore(FY2024)
}

const onDownloadPdf = (examinationId, examinationType, pdfType) => (
  route('result.downloadPdf', {
    examination_id: examinationId,
    examination_type: examinationType,
    pdf_type: pdfType,
  })
)

</script>
